import React, { useEffect, useState } from "react";
import { saveSettings, updateValue } from "../../apis/setting";
import { Setting } from "../../interfaces/Setting";
import { useNotification } from "../../NotificationContext";

interface Props {
  settings: Setting[];
  onRefresh: () => void;
}

const FIELDS = [
  {
    code: "patient_reg_prefix",
    label: "Patient Registration Prefix",
    placeholder: "e.g. DHN",
    hint: "Appears before the year in patient reg numbers.",
    type: "patient" as const,
  },
  {
    code: "visit_opd_prefix",
    label: "OPD Visit Number Prefix",
    placeholder: "OPD",
    hint: "Default is OPD — change only if your clinic uses a different code.",
    type: "opd" as const,
  },
  {
    code: "visit_ipd_prefix",
    label: "IPD Visit Number Prefix",
    placeholder: "IPD",
    hint: "Default is IPD.",
    type: "ipd" as const,
  },
  {
    code: "sequence_digits",
    label: "Sequence Digits (padding)",
    placeholder: "5",
    hint: "How many digits in the number part — 5 gives 00001, 6 gives 000001.",
    type: "digits" as const,
  },
] as const;

const SequenceSettings: React.FC<Props> = ({ settings, onRefresh }) => {
  const { showNotification } = useNotification();

  const find = (code: string) => settings.find((s) => s.code === code);

  const [values, setValues] = useState<Record<string, string>>({
    patient_reg_prefix: find("patient_reg_prefix")?.value ?? "",
    visit_opd_prefix:   find("visit_opd_prefix")?.value   ?? "OPD",
    visit_ipd_prefix:   find("visit_ipd_prefix")?.value   ?? "IPD",
    sequence_digits:    find("sequence_digits")?.value    ?? "5",
  });
  const [saving, setSaving] = useState<Record<string, boolean>>({});

  useEffect(() => {
    setValues({
      patient_reg_prefix: find("patient_reg_prefix")?.value ?? "",
      visit_opd_prefix:   find("visit_opd_prefix")?.value   ?? "OPD",
      visit_ipd_prefix:   find("visit_ipd_prefix")?.value   ?? "IPD",
      sequence_digits:    find("sequence_digits")?.value    ?? "5",
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settings]);

  const save = async (code: string) => {
    setSaving((p) => ({ ...p, [code]: true }));
    try {
      const existing = find(code);
      const value = values[code];
      if (existing) {
        await updateValue({ id: existing.id, value });
      } else {
        await saveSettings({ name: code, code, value, type: "general" });
      }
      showNotification("success", "Saved", "", "topRight");
      onRefresh();
    } catch {
      showNotification("error", "Failed to save", "", "topRight");
    } finally {
      setSaving((p) => ({ ...p, [code]: false }));
    }
  };

  const preview = (code: string) => {
    const prefix   = values["patient_reg_prefix"];
    const opdPfx   = values["visit_opd_prefix"] || "OPD";
    const ipdPfx   = values["visit_ipd_prefix"] || "IPD";
    const digits   = Math.max(3, Math.min(9, parseInt(values["sequence_digits"]) || 5));
    const padded   = "1".padStart(digits, "0");
    const year     = new Date().getFullYear();

    const parts = (pfx: string) =>
      [pfx, year, padded].filter(Boolean).join("/");

    switch (code) {
      case "patient_reg_prefix": return parts(prefix);
      case "visit_opd_prefix":   return parts(opdPfx);
      case "visit_ipd_prefix":   return parts(ipdPfx);
      case "sequence_digits":    return parts(prefix || "PREFIX");
      default: return "";
    }
  };

  return (
    <div className="space-y-6">
      {FIELDS.map(({ code, label, placeholder, hint }) => (
        <div key={code} className="grid grid-cols-1 sm:grid-cols-2 gap-4 items-start">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {label}
            </label>
            <p className="text-xs text-gray-500 mb-2">{hint}</p>
            <div className="flex gap-2">
              <input
                type="text"
                value={values[code]}
                placeholder={placeholder}
                onChange={(e) =>
                  setValues((p) => ({ ...p, [code]: e.target.value.toUpperCase() }))
                }
                className="flex-1 block py-1.5 px-2.5 text-sm border border-gray-300 rounded-md shadow-sm focus:border-primary focus:ring focus:ring-primary/20 focus:outline-none"
              />
              <button
                type="button"
                disabled={saving[code]}
                onClick={() => save(code)}
                className="px-3 py-1.5 text-sm font-medium text-white rounded-md bg-primary hover:bg-hover disabled:opacity-60"
              >
                {saving[code] ? "Saving…" : "Save"}
              </button>
            </div>
          </div>

          <div className="sm:pt-7">
            <p className="text-xs text-gray-500 mb-1">Preview</p>
            <div className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-gray-100 border border-gray-200">
              <span className="font-mono text-sm text-gray-800">{preview(code)}</span>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

export default SequenceSettings;
